import { Button, TextInput } from '@mantine/core';
import { useEffect, useState, type FormEvent } from 'react';
import { ApiError, requestCode, verifyLogin, type ApiFailure } from '../api';
import { countLabel } from '../countLabel';
import {
  clearPendingCode,
  loadPendingCode,
  savePendingCode,
} from '../pendingCode';
import { usePath } from '../router';

const RETRY_FALLBACK_SECONDS = 60;

export function oauthStartPath(
  provider: 'google' | 'microsoft',
  next: string,
): string {
  return `/api/auth/${provider}/start?next=${encodeURIComponent(next)}`;
}

const OAUTH_MESSAGES: Record<string, string> = {
  unknown:
    'We couldn’t sign you in with that account. Contact sdev@ftlovolleyball.ca and we will sort it out.',
  google: 'Google sign-in didn’t finish. Please try again.',
  microsoft: 'Microsoft sign-in didn’t finish. Please try again.',
  state: 'Sign-in didn’t finish. Please try again.',
};

function MicrosoftMark() {
  return (
    <svg viewBox="0 0 18 18" aria-hidden="true" className="size-[18px]">
      <path fill="#F25022" d="M1 1h7.6v7.6H1z" />
      <path fill="#7FBA00" d="M9.4 1H17v7.6H9.4z" />
      <path fill="#00A4EF" d="M1 9.4h7.6V17H1z" />
      <path fill="#FFB900" d="M9.4 9.4H17V17H9.4z" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" aria-hidden="true" className="size-[18px]">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62Z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z"
      />
      <path
        fill="#FBBC05"
        d="M3.97 10.72a5.41 5.41 0 0 1 0-3.44V4.96H.96a9 9 0 0 0 0 8.08l3.01-2.32Z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58C13.46.89 11.43 0 9 0A9 9 0 0 0 .96 4.96l3.01 2.32C4.68 5.16 6.66 3.58 9 3.58Z"
      />
    </svg>
  );
}

function failureOf(error: unknown): ApiFailure {
  return error instanceof ApiError ? error.failure : { kind: 'unexpected' };
}

function requestCodeMessage(error: ApiFailure): string {
  return error.kind === 'network'
    ? 'We couldn’t reach the portal. Check your connection and try again.'
    : 'We couldn’t send a code right now. Please try again in a moment.';
}

function verifyCodeMessage(error: ApiFailure): string {
  if (error.kind === 'invalid') {
    return 'That code didn’t work. Check it and try again.';
  }
  return error.kind === 'network'
    ? 'We couldn’t reach the portal. Your code has not been used; please try again.'
    : 'We couldn’t check that code right now. Please try again.';
}

export function LoginPage({
  onLoggedIn,
  oauthError = null,
  accent = 'brand',
}: {
  onLoggedIn: () => void | Promise<void>;
  oauthError?: string | null;
  /** The action colour: portal orange, or the clinic application's teal. */
  accent?: 'brand' | 'deep';
}) {
  const path = usePath();
  const [pendingEmail] = useState(loadPendingCode);
  const [step, setStep] = useState<'email' | 'code'>(
    pendingEmail === null ? 'email' : 'code',
  );
  const [email, setEmail] = useState(pendingEmail ?? '');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [waitSeconds, setWaitSeconds] = useState(0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (waitSeconds <= 0) return;
    const timer = setTimeout(() => setWaitSeconds(waitSeconds - 1), 1000);
    return () => clearTimeout(timer);
  }, [waitSeconds]);

  async function submitEmail(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestCode(email);
      savePendingCode(email);
      setStep('code');
    } catch (thrown) {
      const failure = failureOf(thrown);
      if (failure.kind === 'rate-limited') {
        setWaitSeconds(failure.retryAfterSeconds || RETRY_FALLBACK_SECONDS);
      } else {
        setError(requestCodeMessage(failure));
      }
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await verifyLogin(email, code);
      clearPendingCode();
      await onLoggedIn();
    } catch (thrown) {
      setError(verifyCodeMessage(failureOf(thrown)));
    } finally {
      setBusy(false);
    }
  }

  const alert =
    waitSeconds > 0
      ? `Too many tries. Wait ${countLabel(waitSeconds, 'second')} and try again.`
      : (error ?? (oauthError ? OAUTH_MESSAGES[oauthError] : null) ?? null);

  return (
    <section
      aria-label="Log in"
      className="border-hairline bg-surface shadow-card mx-auto flex w-full max-w-md flex-col gap-4 rounded-xl border p-5"
    >
      {step === 'email' ? (
        <>
          <form onSubmit={submitEmail} className="flex flex-col gap-4">
            <TextInput
              type="email"
              label="Email"
              description="Use the email you registered with FTLO / Boba League."
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              autoComplete="email"
              required
              // oxlint-disable-next-line jsx-a11y/no-autofocus
              autoFocus
            />
            <Button
              type="submit"
              color={accent}
              loading={busy}
              disabled={waitSeconds > 0}
            >
              Send me a code
            </Button>
          </form>
          <div className="flex items-center gap-3">
            <span className="border-hairline h-px flex-1 border-t" />
            <span className="text-muted text-sm">or</span>
            <span className="border-hairline h-px flex-1 border-t" />
          </div>
          <Button
            component="a"
            href={oauthStartPath('google', path)}
            variant="default"
            leftSection={<GoogleMark />}
          >
            Log in with Google
          </Button>
          <Button
            component="a"
            href={oauthStartPath('microsoft', path)}
            variant="default"
            leftSection={<MicrosoftMark />}
          >
            Log in with Microsoft
          </Button>
        </>
      ) : (
        <form onSubmit={submitCode} className="flex flex-col gap-4">
          <p aria-live="polite">We sent a code to {email}.</p>
          <p className="text-muted text-sm">
            No code yet? Check your spam folder — our email sometimes lands
            there.
          </p>
          <TextInput
            label="Confirmation code"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) =>
              setCode(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))
            }
            maxLength={6}
            required
            // oxlint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          <Button type="submit" color={accent} loading={busy}>
            Log in
          </Button>
          <Button
            variant="subtle"
            color={accent}
            type="button"
            onClick={() => {
              clearPendingCode();
              setStep('email');
              setCode('');
              setError(null);
            }}
          >
            Use a different email
          </Button>
        </form>
      )}
      {alert !== null ? (
        <p role="alert" className="text-red-700">
          {alert}
        </p>
      ) : null}
    </section>
  );
}
